import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import Axios from '../../../foundation/config/axios';
import {
  useAcquireVariantLockMutation,
  useRefreshVariantLockMutation,
  useReleaseVariantLockMutation,
} from '../../api/variantApi';
import { selectUser } from '../../authentication/redux/selectors';
import { selectIsUserInstituteAllowed, selectVariantLock } from '../redux';

/**
 * Global Tracking for Variant Locks
 *
 * Since useVariantLock can be used in multiple components/tabs for the same variant,
 * we need to track how many instances are currently "holding" the lock.
 * Only the first instance triggers the backend 'acquire' and the last one triggers 'release'.
 */
const lockHolders: Record<string | number, number> = {};
const releaseTimers: Record<string | number, number> = {};
const pageHideReleaseSent: Record<string | number, boolean> = {};

/**
 * Cross-Tab Tracking (localStorage)
 *
 * Problem: lockHolders above only knows about the *current* tab. If the same
 * user has the same variant open in two tabs, closing tab A must NOT release
 * the backend lock, because tab B is still editing.
 *
 * Solution: all tabs of the same browser share localStorage. Every tab writes
 * an entry "my tab is viewing variant X" together with a timestamp:
 *   variantLockTabs:<variantId> = { "<tabId>": <lastSeenMs>, ... }
 *
 * - On mount and on every 30s heartbeat, the tab updates its timestamp.
 * - Before releasing the lock (pagehide or unmount), the tab removes its own
 *   entry and checks whether any other tab is still registered. Only the
 *   last remaining tab actually sends the DELETE to the backend.
 * - A tab that crashed can never clean up its own entry, so entries are
 *   ignored once their timestamp is older than TAB_ENTRY_TTL_MS (90s = three
 *   missed heartbeats). A dead tab therefore cannot block the release forever.
 */
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const TAB_ENTRY_TTL_MS = 90 * 1000; // three missed heartbeats

const tabRegistryKey = (variantId: number | string) =>
  `variantLockTabs:${variantId}`;

const readActiveTabs = (variantId: number | string): Record<string, number> => {
  try {
    const raw = window.localStorage.getItem(tabRegistryKey(variantId));
    const entries: Record<string, number> = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    for (const [tabId, lastSeen] of Object.entries(entries)) {
      if (typeof lastSeen !== 'number' || now - lastSeen > TAB_ENTRY_TTL_MS) {
        delete entries[tabId];
      }
    }
    return entries;
  } catch {
    return {};
  }
};

const writeActiveTabs = (
  variantId: number | string,
  entries: Record<string, number>,
) => {
  try {
    const key = tabRegistryKey(variantId);
    if (Object.keys(entries).length === 0) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(entries));
    }
  } catch {
    // localStorage unavailable (e.g. private mode): fall back to single-tab
    // behaviour; the backend TTL + periodic cleanup still cover stale locks.
  }
};

const registerTab = (variantId: number | string) => {
  const entries = readActiveTabs(variantId);
  entries[TAB_ID] = Date.now();
  writeActiveTabs(variantId, entries);
};

const unregisterTab = (variantId: number | string) => {
  const entries = readActiveTabs(variantId);
  delete entries[TAB_ID];
  writeActiveTabs(variantId, entries);
};

const otherTabsHoldVariant = (variantId: number | string) =>
  Object.keys(readActiveTabs(variantId)).some((tabId) => tabId !== TAB_ID);

/**
 * Variant Lock Hook - Manages automatic variant locking
 *
 * Mutation hooks from RTK Query replace the old `dispatch(thunk(...)).unwrap()` pattern.
 * The slice state is updated automatically via matchFulfilled/matchRejected matchers in
 * slice.ts (see RTK Query tutorial Part 8: "Updating Toast Display").
 *
 * @param variantId - The ID of the variant to lock
 */
export const useVariantLock = (variantId?: number | string) => {
  const user = useSelector(selectUser);
  const lock = useSelector(selectVariantLock);
  const currentUser = user?.username;

  const isUserInstituteAllowed = useSelector(selectIsUserInstituteAllowed);

  // RTK Query mutation hooks – trigger functions can be called inside effects
  const [acquireVariantLockMutation] = useAcquireVariantLockMutation();
  const [refreshVariantLockMutation] = useRefreshVariantLockMutation();
  const [releaseVariantLockMutation] = useReleaseVariantLockMutation();

  const timerRef = useRef<number | null>(null);
  const isAcquiringLock = useRef<boolean>(false);
  const hasAcquiredLock = useRef<boolean>(false);
  const latestLockRef = useRef(lock);

  useEffect(() => {
    latestLockRef.current = lock;
  }, [lock]);

  useEffect(() => {
    if (!variantId || !currentUser || !isUserInstituteAllowed) {
      return;
    }
    if (latestLockRef.current?.lock_type === 'refresh') {
      return;
    }

    // --- 1. GLOBAL TRACKING & BUFFER (500ms) ---
    lockHolders[variantId] = (lockHolders[variantId] || 0) + 1;
    pageHideReleaseSent[variantId] = false;
    registerTab(variantId);

    if (releaseTimers[variantId]) {
      window.clearTimeout(releaseTimers[variantId]);
      delete releaseTimers[variantId];
    }

    // --- 2. LOCK ACQUISITION LOGIC ---
    const performAcquireLock = async () => {
      if (isAcquiringLock.current || hasAcquiredLock.current) {
        return;
      }
      try {
        isAcquiringLock.current = true;
        // Calling the RTK Query mutation trigger – same as tutorial's
        // `await addNewPost({...}).unwrap()` pattern (Part 7).
        const result = await acquireVariantLockMutation({
          variant_id: variantId,
        }).unwrap();

        if (result?.locked_by === currentUser) {
          hasAcquiredLock.current = true;
        }
      } catch (error: any) {
        // error is the transformErrorResponse result: { locked_by, message }
        // (see acquireVariantLock endpoint in variantApi.ts)
        if (error?.locked_by && error?.locked_by !== currentUser) {
          hasAcquiredLock.current = false;
        }
      } finally {
        isAcquiringLock.current = false;
      }
    };

    if (!hasAcquiredLock.current) {
      performAcquireLock();
    }

    // --- 3. HEARTBEAT (30s) ---
    timerRef.current = window.setInterval(async () => {
      if (latestLockRef.current?.lock_type === 'refresh') {
        return;
      }
      const currentLockOwner = latestLockRef.current?.locked_by;

      if (!currentLockOwner || currentLockOwner === currentUser) {
        registerTab(variantId);
        try {
          await refreshVariantLockMutation({ variant_id: variantId }).unwrap();
        } catch (error: any) {
          const status = error?.status;
          if (status === 404) {
            // SELF-HEALING: lock was lost, try to re-acquire
            hasAcquiredLock.current = false;
            performAcquireLock();
          } else if (status === 403 && timerRef.current) {
            // STOP: another user took the lock
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }
    }, 30 * 1000);

    // --- 4. CLEANUP ---
    // On tab close/navigation the browser kills in-flight XHRs (axios), so the
    // DELETE must be sent as a keepalive fetch, which the browser is allowed to
    // finish after the page is gone. 'pagehide' is more reliable than
    // 'beforeunload' (fires on mobile and bfcache navigations too).
    const releaseLockOnPageHide = () => {
      const csrfToken = document.cookie.match(/(^|;\s*)csrftoken=([^;]*)/)?.[2];
      void fetch(
        `${Axios.defaults.baseURL ?? ''}/variantdata/variant/${variantId}/lock`,
        {
          method: 'DELETE',
          keepalive: true,
          credentials: 'include',
          headers: csrfToken
            ? { 'X-CSRFToken': decodeURIComponent(csrfToken) }
            : undefined,
        },
      );
    };
    const handlePageHide = () => {
      unregisterTab(variantId);
      if (
        latestLockRef.current?.lock_type !== 'refresh' &&
        latestLockRef.current?.locked_by === currentUser &&
        !pageHideReleaseSent[variantId] &&
        !otherTabsHoldVariant(variantId)
      ) {
        pageHideReleaseSent[variantId] = true;
        releaseLockOnPageHide();
      }
    };
    window.addEventListener('pagehide', handlePageHide);

    // Restored from the back-forward cache: the lock may have been released
    // on pagehide (or taken over meanwhile), so re-register this tab and
    // re-acquire instead of silently resuming without a lock.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }
      pageHideReleaseSent[variantId] = false;
      registerTab(variantId);
      hasAcquiredLock.current = false;
      performAcquireLock();
    };
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      lockHolders[variantId] -= 1;

      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);

      hasAcquiredLock.current = false;
      isAcquiringLock.current = false;

      if (lockHolders[variantId] === 0) {
        unregisterTab(variantId);
      }

      // ONLY RELEASE AFTER 500ms (to survive Navigation/Refresh/Remount)
      if (
        latestLockRef.current?.lock_type !== 'refresh' &&
        latestLockRef.current?.locked_by === currentUser &&
        lockHolders[variantId] === 0 &&
        !otherTabsHoldVariant(variantId)
      ) {
        releaseTimers[variantId] = window.setTimeout(() => {
          // Re-check: another tab may have (re-)registered during the buffer.
          if (!otherTabsHoldVariant(variantId)) {
            releaseVariantLockMutation({ variant_id: variantId });
          }
          delete releaseTimers[variantId];
        }, 500);
      }
    };
  }, [
    variantId,
    currentUser,
    isUserInstituteAllowed,
    acquireVariantLockMutation,
    refreshVariantLockMutation,
    releaseVariantLockMutation,
    lock?.lock_type,
  ]);
};
